import { getRouteApi } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import chatTopGradientUrl from "@/assets/svg/chatTopGradient.svg?url";
import chatTopGradientHalloweenUrl from "@/assets/svg/chatTopGradientHalloween.svg?url";
import HeraldChat from "@/components/herald-chat/HeraldChat";
import HeraldChatControlPanel from "@/components/herald-chat/HeraldChatControlPanel";
import HeraldChatHistory from "@/components/herald-chat/herald-chat-history-sidebar/HeraldChatHistory";
import HeraldChatModals from "@/components/herald-chat/modals/HeraldChatModals";
import { PageHeader } from "@/components/layouts/PageHeader";
import { HOLIDAY_THEME } from "@/lib/constants/holiday-theme";
import useFeature from "@/lib/feature-management/useFeature";
import { resolveHeraldChatTitleConversationId } from "@/lib/hooks/chat/resolveHeraldChatTitleConversationId";
import { logoutUser } from "@/store/auth.async-actions";
import useAuth, { type AuthState } from "@/store/auth.slice";
import { useHeraldConversationTitle } from "@/store/herald-chat/herald-chat-history.selectors";
import useHeraldChatSettingsStore from "@/store/herald-chat/herald-chat-settings.slice";

const LARGE_SCREEN_BREAKPOINT = 1024;

const chatRouteApi = getRouteApi("/_authenticated/chat/$conversationId");

/**
 * `/chat` route for TanStack Router. Renders Herald chat UI.
 */
const ChatRoute = () => {
  const user = useAuth((state: AuthState) => state.user);
  const showSidebar = useFeature("herald-chat-history-sidebar", false);
  const isHeraldChatEmpty = useHeraldChatSettingsStore(
    (state) => state.isHeraldChatEmpty,
  );
  const { conversationId: conversationIdParam } = chatRouteApi.useParams();
  const activeConversationId = useHeraldChatSettingsStore(
    (state) => state.activeConversationId,
  );
  const conversationTitle = useHeraldConversationTitle(
    resolveHeraldChatTitleConversationId(
      conversationIdParam,
      activeConversationId,
    ),
  );

  const [isHistorySidebarExpanded, setIsHistorySidebarExpanded] = useState(
    () =>
      typeof window !== "undefined" &&
      window.innerWidth >= LARGE_SCREEN_BREAKPOINT,
  );

  const handleShowHistory = useCallback(() => {
    setIsHistorySidebarExpanded(true);
  }, []);

  return (
    <>
      <HeraldChatModals />
      <PageHeader
        label="Chat"
        showFeedSwitcher={false}
        userEmail={user?.email ?? ""}
        userTenantName={user?.default_tenant?.name}
        onLogout={logoutUser}
      />
      <main className="relative flex h-[calc(100vh-57px)] min-w-0 flex-1 overflow-hidden bg-white">
        {showSidebar && (
          <div className="relative z-2 shrink-0">
            <HeraldChatHistory
              isExpanded={isHistorySidebarExpanded}
              onExpandedChange={setIsHistorySidebarExpanded}
            />
          </div>
        )}
        <div className="relative z-2 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-transparent">
          {isHeraldChatEmpty ? (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[min(50vh,420px)] overflow-hidden"
            >
              <img
                src={
                  HOLIDAY_THEME === "halloween"
                    ? chatTopGradientHalloweenUrl
                    : chatTopGradientUrl
                }
                alt=""
                className="h-[344px] w-full min-w-full max-w-none object-cover object-top"
                width={1280}
                height={344}
                decoding="async"
              />
            </div>
          ) : null}
          <HeraldChatControlPanel
            hasHistoryControl={showSidebar}
            isHistorySidebarExpanded={isHistorySidebarExpanded}
            onShowHistory={handleShowHistory}
            conversationTitle={conversationTitle}
          />
          <div className="relative z-1 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <HeraldChat />
          </div>
        </div>
      </main>
    </>
  );
};

export default ChatRoute;
